import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { useAuth } from '../context/AuthContext';
import { marketplaceApi, personApi, projectApi } from '../services/api';
import Modal from '../components/Modal';
import {
  ProviderRequestFields, emptyProviderRequestDraft, isProviderRequestDraftComplete, type ProviderRequestDraft
} from '../components/ProviderRequestFields';
import type { Project, ProviderRequest } from '../types';
import { ExpertCard, SavedExpertAvatar } from '../components/ExpertCard';
import { ExpertFinder } from '../components/ExpertFinder';
import {
  displayFirm, expertFirm as firmOf, expertPresentation as presentationOf,
  type Expert as Provider, type ExpertType as ProviderType
} from '../utils/expertProfile';

type MineStatus = 'invited' | 'accepted' | 'pending';

/** One expert under "Mina experter": a request of the user's on one project. */
interface MineEntry {
  key: string;
  providerId: string;
  provider?: Provider;
  type: ProviderType;
  name: string;
  firm: string;
  project: string;
  projectId: string;
  status: MineStatus;
}

/** One row under "Mina experter": an expert with every project asked about. */
interface MineExpert {
  providerId: string;
  provider?: Provider;
  type: ProviderType;
  name: string;
  firm: string;
  entries: MineEntry[];
}

const TYPES: ProviderType[] = ['lawyer', 'inspector'];
// A later step wins when the same expert has several requests on one project.
const STATUS_RANK: Record<MineStatus, number> = { pending: 0, accepted: 1, invited: 2 };
const isMineStatus = (status: ProviderRequest['status']): status is MineStatus => status in STATUS_RANK;

const projectLabel = (number: number | undefined, description: string | undefined) =>
  [number ? `#${number}` : '', description ?? ''].filter(Boolean).join(' ');

/**
 * Extern hjälp (ClickUp 869fh4y1m): one page without tab rows. "Mina experter"
 * lists the lawyers and inspectors the user has asked on a project, with the
 * request's state; "Hitta hjälp" picks a kind of expert, lists the available
 * ones and sends a request to a chosen project straight from a dialog.
 */
export function Marketplace() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [providers, setProviders] = useState<Record<ProviderType, Provider[]>>({ lawyer: [], inspector: [] });
  const [requests, setRequests] = useState<ProviderRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [type, setType] = useState<ProviderType>('lawyer');
  const [openProfile, setOpenProfile] = useState<string | null>(null);
  const [openMine, setOpenMine] = useState<string | null>(null);
  const [notice, setNotice] = useState('');

  // The parties of a project (private person or company) can ask an expert.
  // Company = any non-admin account that isn't person/lawyer/inspector, as in
  // the project view.
  const isPerson = user?.personaType === 'person';
  const canInvite = isPerson || (
    !!user && !user.isAdmin
    && user.personaType !== 'lawyer'
    && user.personaType !== 'inspector'
    && user.personaType !== 'admin'
  );

  const loadRequests = () => canInvite
    ? marketplaceApi.getMyRequests().then(setRequests).catch(() => {})
    : Promise.resolve();

  // The user's ongoing projects, to know which ones an expert can still be
  // asked about.
  const [projects, setProjects] = useState<Project[] | null>(null);

  useEffect(() => {
    Promise.all([
      ...TYPES.map(kind => marketplaceApi.getProviders(kind)
        .then((list: Provider[]) => setProviders(current => ({ ...current, [kind]: list })))
        .catch(() => {})),
      loadRequests(),
    ]).finally(() => setIsLoading(false));
    if (canInvite) {
      (isPerson ? personApi.getProjects() : projectApi.getProjects())
        .then((list: Project[]) => setProjects(list.filter(project => project.status !== 'completed')))
        .catch(() => setProjects([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mine = useMemo<MineEntry[]>(() => {
    const byKey = new Map<string, MineEntry>();
    for (const request of requests) {
      if (request.requesterUserId !== user?.id || !isMineStatus(request.status)) continue;
      const kind = request.providerType;
      const provider = providers[kind]?.find(item => item.id === request.providerId);
      const entry: MineEntry = {
        key: `${request.providerId}:${request.projectId}`,
        providerId: request.providerId,
        provider,
        type: kind,
        name: provider?.name ?? request.providerName ?? request.recipientName ?? '',
        firm: provider ? displayFirm(provider.name, firmOf(provider, kind)) : '',
        project: projectLabel(request.projectNumber, request.projectDescription),
        projectId: request.projectId,
        status: request.status,
      };
      const previous = byKey.get(entry.key);
      if (!previous || STATUS_RANK[entry.status] > STATUS_RANK[previous.status]) byKey.set(entry.key, entry);
    }
    return Array.from(byKey.values());
  }, [requests, providers, user?.id]);

  const mineExperts = useMemo<MineExpert[]>(() => {
    const byProvider = new Map<string, MineExpert>();
    for (const entry of mine) {
      const expert = byProvider.get(entry.providerId)
        ?? { providerId: entry.providerId, provider: entry.provider, type: entry.type, name: entry.name, firm: entry.firm, entries: [] };
      expert.entries.push(entry);
      byProvider.set(entry.providerId, expert);
    }
    return Array.from(byProvider.values());
  }, [mine]);

  // Ongoing projects this expert has no open request on, so it can be asked there too.
  const projectsLeftFor = (providerId: string) => (projects ?? []).filter(project =>
    !mine.some(entry => entry.providerId === providerId && entry.projectId === project.id));
  const canInviteTo = (provider: Provider | undefined) =>
    canInvite && !!provider?.availability && projectsLeftFor(provider.id).length > 0;


  // "Bjud in till projekt": a dialog with the projects left for this expert.
  const [inviting, setInviting] = useState<{ provider: Provider; type: ProviderType } | null>(null);
  const [inviteProject, setInviteProject] = useState('');
  const [inviteDraft, setInviteDraft] = useState<ProviderRequestDraft>(emptyProviderRequestDraft);
  const [sending, setSending] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const idempotencyKey = useRef<string | null>(null);

  const inviteChoices = useMemo(() => !inviting || !projects ? [] : projects.filter(project =>
    !mine.some(entry => entry.providerId === inviting.provider.id && entry.projectId === project.id)), [inviting, projects, mine]);

  useEffect(() => {
    if (inviteChoices.length && !inviteChoices.some(project => project.id === inviteProject)) setInviteProject(inviteChoices[0].id);
  }, [inviteChoices, inviteProject]);

  const openInvite = (provider: Provider, kind: ProviderType) => {
    setInviting({ provider, type: kind });
    setInviteDraft(emptyProviderRequestDraft());
    setInviteError('');
    setNotice('');
    idempotencyKey.current = null;
  };

  const sendInvite = async () => {
    if (!inviting || !inviteProject || !isProviderRequestDraftComplete(inviteDraft) || sending) return;
    idempotencyKey.current = idempotencyKey.current ?? crypto.randomUUID();
    setSending(true);
    setInviteError('');
    try {
      await marketplaceApi.createRequest({
        idempotencyKey: idempotencyKey.current,
        projectId: inviteProject,
        providerType: inviting.type,
        providerId: inviting.provider.id,
        serviceType: inviteDraft.serviceType,
        description: inviteDraft.description.trim(),
        timeframe: inviteDraft.timeframe,
      });
      await loadRequests();
      setNotice(t('externalHelp.invite.sent', { name: inviting.provider.name }));
      setInviting(null);
    } catch {
      setInviteError(t('externalHelp.invite.error'));
    } finally {
      setSending(false);
    }
  };

  const statusLabel = (entry: MineEntry) => t(`externalHelp.status.${entry.status}`);
  const statusClass = (status: MineStatus) => status === 'pending' ? 'is-waiting' : 'is-ok';


  // Area, languages and specialties, plus the presentation when the profile is open.
  const renderFacts = (provider: Provider) => (
    <>
      {(provider.geographicArea || provider.languages?.length > 0) && (
        <p className="external-help-facts">
          {provider.geographicArea && <span><i className="feather-map-pin" aria-hidden="true"></i>{provider.geographicArea}</span>}
          {provider.languages?.length > 0 && <span><i className="feather-message-circle" aria-hidden="true"></i>{provider.languages.join(', ')}</span>}
        </p>
      )}
      {provider.specialties?.length > 0 && (
        <ul className="external-help-tags" aria-label={t('externalHelp.specialties')}>
          {provider.specialties.map(specialty => <li key={specialty}>{specialty}</li>)}
        </ul>
      )}
    </>
  );
  const renderPresentation = (provider: Provider | undefined, kind: ProviderType) => {
    const presentation = provider && presentationOf(provider, kind);
    return presentation
      ? <div className="ql-editor external-help-presentation" dangerouslySetInnerHTML={{ __html: presentation }} />
      : <p className="external-help-presentation external-help-meta">{t('externalHelp.noPresentation')}</p>;
  };

  const renderExpert = (layout: 'card' | 'row') => (provider: Provider) => {
    const entries = mine.filter(entry => entry.providerId === provider.id);
    const expanded = openProfile === provider.id;
    return (
      <ExpertCard
        key={provider.id}
        id={`expert-${provider.id}`}
        testId="external-help-expert"
        provider={provider}
        type={type}
        layout={layout}
        statuses={entries.map(entry => ({ key: entry.key, label: statusLabel(entry), tone: statusClass(entry.status) === 'is-waiting' ? 'waiting' : 'ok', meta: entry.project }))}
        action={canInviteTo(provider) ? {
          label: t(entries.length > 0 ? 'externalHelp.inviteMore' : 'marketplace.steps.inviteToProject'),
          icon: 'user-plus',
          onClick: () => openInvite(provider, type),
          testId: 'marketplace-invite'
        } : undefined}
        expanded={expanded}
        onToggleProfile={() => setOpenProfile(expanded ? null : provider.id)}
      />
    );
  };

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('externalHelp.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: t('externalHelp.title') }
          ]} />
        </div>
        {/* Long for the header row on a phone or tablet: shown above the sections there. */}
        <div className="page-header-right ms-auto d-none d-lg-block">
          <p className="fw-bold text-muted mb-0">{t('externalHelp.subtitle')}</p>
        </div>
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      ) : (
        <div className="external-help">
          <p className="external-help-lead d-lg-none">{t('externalHelp.subtitle')}</p>
          {notice && <div className="alert alert-success mb-0" role="status">{notice}</div>}

          {canInvite && (
            <section className="card external-help-section" aria-labelledby="external-help-mine" data-testid="external-help-mine">
              <div className="external-help-section-head">
                <h3 id="external-help-mine">{t('externalHelp.mine.title')} <span>({mineExperts.length})</span></h3>
                <p>{t('externalHelp.mine.description')}</p>
              </div>
              {mineExperts.length === 0 ? (
                <p className="external-help-empty">{t('externalHelp.mine.empty')}</p>
              ) : (
                <ul className="external-help-mine-list">
                  {mineExperts.map(expert => {
                    const expanded = openMine === expert.providerId;
                    const profileId = `external-help-mine-profile-${expert.providerId}`;
                    return (
                      <li key={expert.providerId} className="external-help-mine-row">
                        <SavedExpertAvatar provider={expert.provider} type={expert.type} name={expert.name} />
                        <div className="external-help-mine-who">
                          <p className="external-help-name">{expert.name}</p>
                          <p className="external-help-meta">{[t(`externalHelp.role.${expert.type}`), expert.firm].filter(Boolean).join(' · ')}</p>
                          <ul className="external-help-mine-projects" aria-label={t('externalHelp.mine.projects')}>
                            {expert.entries.map(entry => (
                              <li key={entry.key}>
                                <span className={`external-help-pill ${statusClass(entry.status)}`}>{statusLabel(entry)}</span>
                                <span>{entry.project}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="external-help-mine-end">
                          {expert.provider && canInviteTo(expert.provider) && (
                            <button type="button" className="external-help-link" onClick={() => openInvite(expert.provider!, expert.type)} data-testid="external-help-invite-more">
                              {t('externalHelp.inviteMore')}
                            </button>
                          )}
                          <button
                            type="button"
                            className="external-help-link"
                            aria-expanded={expanded}
                            aria-controls={profileId}
                            onClick={() => setOpenMine(expanded ? null : expert.providerId)}
                          >
                            {t(expanded ? 'externalHelp.hideProfile' : 'externalHelp.showProfile')}
                          </button>
                        </div>
                        {expanded && (
                          <div id={profileId} className="external-help-mine-profile" data-testid="external-help-mine-profile">
                            {expert.provider && renderFacts(expert.provider)}
                            {renderPresentation(expert.provider, expert.type)}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}

          <ExpertFinder
            type={type}
            onTypeChange={setType}
            providers={providers[type]}
            renderExpert={(provider, layout) => renderExpert(layout)(provider)}
          />
        </div>
      )}

      {inviting && (
        <Modal
          title={`${t('marketplace.request.title')} — ${inviting.provider.name}`}
          onClose={() => setInviting(null)}
          fullscreenBelow="sm"
          footer={projects && inviteChoices.length > 0 ? (
            <>
              <button type="button" className="btn btn-light" onClick={() => setInviting(null)}>{t('common.cancel')}</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void sendInvite()}
                disabled={sending || !inviteProject || !isProviderRequestDraftComplete(inviteDraft)}
                data-testid="external-help-send"
              >
                {sending && <span className="spinner-border spinner-border-sm me-2"></span>}
                <i className="feather-send me-2"></i>
                {t(sending ? 'externalHelp.invite.sending' : 'marketplace.request.submit')}
              </button>
            </>
          ) : undefined}
        >
          {projects === null ? (
            <div className="d-flex justify-content-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('common.loading')}</span>
              </div>
            </div>
          ) : inviteChoices.length === 0 ? (
            <p className="text-muted mb-0">{t(projects.length === 0 ? 'marketplace.noProjects' : 'externalHelp.invite.allAsked')}</p>
          ) : (
            <>
              {inviteError && <div className="alert alert-danger" role="alert">{inviteError}</div>}
              <ProviderRequestFields
                providerType={inviting.type}
                value={inviteDraft}
                onChange={setInviteDraft}
                before={(
                  <div className="mb-3">
                    <label htmlFor="external-help-project" className="form-label fw-semibold">{t('externalHelp.invite.project')}</label>
                    <select id="external-help-project" className="form-select service-type-select" value={inviteProject} onChange={event => setInviteProject(event.target.value)}>
                      {inviteChoices.map(project => (
                        <option key={project.id} value={project.id}>{projectLabel(project.projectNumber, project.description)}</option>
                      ))}
                    </select>
                  </div>
                )}
              />
            </>
          )}
        </Modal>
      )}
    </DashboardLayout>
  );
}
